// Lightweight auth store backed by localStorage. We don't pull in
// zustand/redux just for this — a couple of plain functions + a
// useSyncExternalStore hook covers our needs.

const TOKEN_KEY = 'cherrypicked.pro.token';
const USER_KEY = 'cherrypicked.pro.user';

export type AuthUser = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  status:
    | 'ONBOARDING_INPROGRESS'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'SUSPENDED'
    | 'DELETED';
  email_verified_at: string | null;
  // Onboarding position cached from /professional/me so guards can route a
  // returning pro to the right stage: 0 = verify-identity, 1..10 = wizard.
  onboarding_step?: number | null;
};

const listeners = new Set<() => void>();

function emit() {
  try {
    for (const listener of listeners) {
      try {
        listener();
      } catch {
        /* listener crashing shouldn't break others */
      }
    }
  } catch (e) {
    console.error('Error emitting auth change:', e);
  }
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    console.error('Error getting token:', e);
    return null;
  }
}

// useSyncExternalStore compares snapshots with Object.is — if we returned
// a freshly-parsed object on every call, React would loop forever. Cache
// the parsed value and only re-parse when the underlying raw JSON string
// actually changes.
let userCache: { raw: string | null; parsed: AuthUser | null } = {
  raw: null,
  parsed: null,
};

export function getUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    if (raw === userCache.raw) return userCache.parsed;
    const parsed = raw ? (JSON.parse(raw) as AuthUser) : null;
    userCache = { raw, parsed };
    return parsed;
  } catch (e) {
    console.error('Error getting user:', e);
    return null;
  }
}

export function setAuth(token: string, user: AuthUser) {
  try {
    // Only one role can hold an active session at a time. Wipe any
    // customer token sitting in the same browser so the website's
    // header / route guards don't see both flows as authenticated.
    // We poke localStorage directly (rather than importing
    // clearCustomerAuth) to keep this module dependency-free.
    window.localStorage.removeItem('cherrypicked.customer.token');
    window.localStorage.removeItem('cherrypicked.customer.user');
    window.localStorage.setItem(TOKEN_KEY, token);
    window.localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (e) {
    console.error('Error setting auth:', e);
  } finally {
    emit();
  }
}

export function patchUser(patch: Partial<AuthUser>) {
  const current = getUser();
  if (!current) return;
  const next = { ...current, ...patch };
  try {
    window.localStorage.setItem(USER_KEY, JSON.stringify(next));
  } catch (e) {
    console.error('Error patching user:', e);
  } finally {
    emit();
  }
}

export function clearAuth() {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(USER_KEY);
  } catch (e) {
    console.error('Error clearing auth:', e);
  } finally {
    emit();
  }
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isAuthenticated(): boolean {
  return !!getToken();
}
